import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {firstValueFrom, forkJoin} from 'rxjs';
import { first } from 'rxjs/operators';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, filePath, SwalService, UIExportService } from '@atmi/core';
import { cardS } from '@atmi/core/models';
import { SettingsApiService } from '@atmi/core/services/settings-api.service';
import { IndexQueryStringType, toFirstUppercase } from '@atmi/core/utils';
import { CardSFiltersComponent } from '@atmi/shared/filters/card-s-filters/card-s-filters.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-cards-s',
  styleUrls: ['./cards-s.component.scss'],
  templateUrl: './cards-s.component.html',
})
export class CardsSComponent extends BaseTableFilteredComponent<cardS.CardS, cardS.CardSFilter> implements UiTableComponent<cardS.CardS> {

  readonly MobileFiltersComponent = CardSFiltersComponent;

  readonly fields: TableField<cardS.CardS>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'SGT.SGTT',
      sortable: true,
    },
    {
      context: (x) => x.MTC.map((mtc) => toFirstUppercase(mtc)).join(', '),
      header: this.translate.instant('Materia e Tecnica'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'MTC',
    },
    {
      context: (x) => `H: ${x.MIS.MISA || '-'} L: ${x.MIS.MISL || '-'} ${x.MIS.MISU}`,
      header: this.translate.instant('Dimensioni'),
      key: 'MIS',
    },
    {
      context: (x) => x.DT.length ? x.DT[0].DTS.DTSI : '-',
      header: this.translate.instant('Anno'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'DT.DTS.DTSI',
      sortable: true,
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  currentFilters: IndexQueryStringType = {};

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    sort = sort && sort.length > 0 ? sort : [{ field: 'SGT.SGTT', type: 'asc' }];
    this.currentFilters = { sort, filter };
    return this.cardsApi.indexS({ sort, page, limit, filter });
  });

  readonly contextMenus: Array<TableContextMenu<cardS.CardS>> = [];

  readonly isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className === 'BeneMI');

  defaultLimit = 300;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly roleService: RoleService,
    private readonly settingsApi: SettingsApiService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, cardS.CardSFiltersMeta, swalService, 's/manager');

    if (this.roleService.hasCapability('BeneS', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    }

    if (this.roleService.hasCapability('BeneS', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id, item) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.cardsApi.removeS(id, 's');
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => this.cardsApi.removeS(id));
    return forkJoin(deleteApis);
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'BeneS');
    if (success) {
      this.selectedItems = [];
    }
  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string, format?: string) => {
        const selection = this.selectedItemIds.length ? this.selectedItemIds : undefined;
        const blob = await firstValueFrom(this.cardsApi.exportList('s', template, format, this.currentFilters, selection).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'BeneS',
      `beni-s`,
    );
  }

  async deleteHandler(id: string, item?: cardS.CardS) {
    return super.deleteHandler(id, item);
  }
}
